import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ArrowLeftRight, BookOpen, Building2, CalendarCheck, ChartColumn, Coins, DatabaseBackup, FileText, History, Inbox, Landmark, LayoutDashboard, ListTree, Package, Palette, Percent, Receipt, Repeat, ScrollText, Tags, Truck, Upload, UserCog, Users as UsersIcon } from 'lucide-react';
import { AppShell, Banner, PageHeader, Toast, type LinkComponent, type NavGroup } from '@elysian/ui';
import { Overview } from './screens/Overview';
import { BillInbox } from './screens/bills/BillInbox';
import { BillReview } from './screens/bills/BillReview';
import { Bank } from './screens/bank/Bank';
import { BankTransactions } from './screens/bank/BankTransactions';
import { BankAccounts } from './screens/bank/BankAccounts';
import { Recurring } from './screens/sales/Recurring';
import { Statements } from './screens/sales/Statements';
import { Backups } from './screens/settings/Backups';
import { Currencies } from './screens/settings/Currencies';
import { Import } from './screens/settings/Import';
import { Users } from './screens/settings/Users';
import { Organisation } from './screens/settings/Organisation';
import { VatCodes } from './screens/settings/VatCodes';
import { SETUP_LATER_KEY, Setup } from './screens/settings/Setup';
import { VatReturns } from './screens/vat/VatReturns';
import { ScreenErrorBoundary } from './screens/shared/ScreenError';
import { YearEnd } from './screens/yearend/YearEnd';
import { AuditTrail } from './screens/audit/AuditTrail';
import { ChartOfAccounts } from './screens/accounting/ChartOfAccounts';
import { Journals } from './screens/accounting/Journals';
import { BillPage, Bills } from './screens/bills/Bills';
import { Reports } from './screens/reports/Reports';
import { ContactList, ContactPage } from './screens/contacts/Contacts';
import { ReceiptPage } from './screens/contacts/ReceiptPage';
import { ImportInvoices } from './screens/invoices/ImportInvoices';
import { InvoiceEditor } from './screens/invoices/InvoiceEditor';
import { InvoicesList } from './screens/invoices/InvoicesList';
import { InvoiceView } from './screens/invoices/InvoiceView';
import { InvoiceDesign } from './screens/settings/InvoiceDesign';
import { AcceptInvite } from './screens/auth/AcceptInvite';
import { AuthCallback } from './screens/auth/AuthCallback';
import { AuthLayout } from './screens/auth/AuthLayout';
import { Organisations } from './screens/auth/Organisations';
import { Search } from './screens/search/Search';
import { Products } from './screens/products/Products';
import type { ShellStatus } from './data/source';
import { SignIn } from './screens/auth/SignIn';
import { useSession } from './state/session';
import { useToast } from './state/toast';

const RouterLink = Link as unknown as LinkComponent;

// Screens arrive in the order set out in PLAN.md; until then a route shows
// which day it is scheduled for.
const NAV: (NavGroup & { soon?: Record<string, string> })[] = [
  { items: [{ to: '/', label: 'Overview', icon: <LayoutDashboard /> }] },
  { label: 'Sales', items: [
    { to: '/invoices', label: 'Invoices', icon: <FileText /> },
    { to: '/statements', label: 'Statements', icon: <ScrollText /> },
    { to: '/customers', label: 'Customers', icon: <UsersIcon /> },
    { to: '/products', label: 'Products & services', icon: <Package /> },
  ] },
  { label: 'Purchases', items: [
    { to: '/inbox', label: 'Bill inbox', icon: <Inbox /> },
    { to: '/bills', label: 'Bills', icon: <Receipt /> },
    { to: '/suppliers', label: 'Suppliers', icon: <Truck /> },
  ] },
  { label: 'Banking', items: [
    { to: '/bank', label: 'Reconcile', icon: <ArrowLeftRight /> },
    { to: '/bank-accounts', label: 'Bank accounts', icon: <Landmark /> },
  ] },
  { label: 'Tax & reports', items: [
    { to: '/vat', label: 'VAT returns', icon: <Percent /> },
    { to: '/reports', label: 'Reports', icon: <ChartColumn /> },
  ] },
  { label: 'Accounting', items: [
    { to: '/recurring', label: 'Recurring', icon: <Repeat /> },
    { to: '/journals', label: 'Journals', icon: <BookOpen /> },
    { to: '/accounts', label: 'Chart of accounts', icon: <ListTree /> },
    { to: '/year-end', label: 'Year end & periods', icon: <CalendarCheck /> },
    { to: '/audit', label: 'Audit trail', icon: <History /> },
  ] },
  { label: 'Settings', items: [
    { to: '/settings/organisation', label: 'Organisation', icon: <Building2 /> },
    { to: '/settings/vat-codes', label: 'VAT codes', icon: <Tags /> },
    { to: '/settings/invoice-design', label: 'Invoice design', icon: <Palette /> },
    { to: '/settings/currencies', label: 'Currencies', icon: <Coins /> },
    { to: '/settings/users', label: 'Users & access', icon: <UserCog /> },
    { to: '/settings/import', label: 'Import data', icon: <Upload /> },
    { to: '/settings/backup', label: 'Backup & restore', icon: <DatabaseBackup /> },
  ] },
];


/** The count beside a menu item, from the live status; nothing when there's nothing to do. */
function badgeFor(to: string, s: ShellStatus | null): { text: string; tone: 'warn' } | undefined {
  if (!s) return undefined;
  const c = s.counts;
  const n = to === '/invoices' ? c.overdue_invoices : to === '/inbox' ? c.inbox_to_check : to === '/bank' ? c.bank_lines
    : to === '/recurring' ? c.recurring_to_approve : 0;
  if (!n) return undefined;
  const word = to === '/invoices' ? ' overdue' : to === '/inbox' ? ' to check' : to === '/recurring' ? ' to approve' : '';
  return { text: `${n}${word}`, tone: 'warn' };
}

function NotFound() {
  return (
    <>
      <PageHeader title="Page not found" />
      <p className="el-muted">There’s nothing at this address. <Link to="/">Back to the overview</Link></p>
    </>
  );
}

function setupLater() {
  try { return sessionStorage.getItem(SETUP_LATER_KEY) === '1'; } catch { return false; }
}

// Pages that work without choosing books (and, apart from the invitation's own
// action, without signing in).
const PUBLIC = ['/sign-in', '/auth/callback', '/invitations/accept'];

export function App() {
  const { context, user, status, start, me, orgId, chooseOrg, signOut, authConfig } = useSession();
  const { pathname, search, key: locationKey } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    void start().catch(() => undefined);
  }, [start]);
  // A new screen opens at its top (#3). Back and forward are left alone, so the browser can return you
  // to where you were.
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    if (navigationType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  const toast = useToast((t) => t.message);
  // Menu counts, the financial year and connected apps; refreshed as people move around.
  const [shell, setShell] = useState<ShellStatus | null>(null);
  const source = useSession((st) => st.source);
  const ready = status === 'ready' && Boolean(context);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    Promise.resolve().then(() => source.status()).then((x) => live && setShell(x), () => undefined); // never breaks the app
    return () => { live = false; };
  }, [source, ready, pathname, orgId]);

  // New books open into the first-run setup. Choosing books and going to '/' happen in separate steps, so a
  // redirect can be undone by a later navigate('/') in the same render batch: the location key catches that.
  const setupDue = status === 'ready' && context?.organisation.setup_completed === false;
  useEffect(() => {
    if (setupDue && pathname === '/' && !setupLater()) navigate('/setup', { replace: true });
  }, [setupDue, pathname, locationKey, navigate]);

  if (PUBLIC.includes(pathname)) {
    return (
      <Routes>
        <Route path="/sign-in" element={status === 'signed_out' || status === 'starting' ? <SignIn /> : <Navigate to="/" replace />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invitations/accept" element={<AcceptInvite />} />
      </Routes>
    );
  }
  if (status === 'starting') return <AuthLayout title="Elysian Ledger"><p className="el-muted" style={{ margin: 0 }}>Loading…</p></AuthLayout>;
  if (status === 'signed_out') return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(pathname + search)}`} replace />;
  if (status === 'no_books' || status === 'choose_books') return <Organisations />;

  const org = context?.organisation;
  // New books go through the first-run setup (unless put off for this tab); books from before it existed count as set up.
  const needsSetup = org?.setup_completed === false;
  const isCurrent = (to: string) => pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));
  // The audit trail is for those who answer for the books (owners, admins, accountants).
  const shown = (to: string) => to !== '/audit' || (context?.capabilities.includes('audit.read') ?? false);
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => shown(i.to))
    .map((i) => ({ ...i, current: isCurrent(i.to), badge: badgeFor(i.to, shell) })) }));
  const connected = shell?.connections.map((c) => c.name) ?? [];
  const memberships = me?.organisations ?? [];

  return (
    <AppShell
      orgName={org?.legal_name ?? 'Loading…'}
      orgTag={org ? `${shell?.fiscal_year?.label ?? `FY${new Date().getFullYear()}`} · ${org.base_currency}` : '…'}
      userName={user.name}
      userInitials={user.initials}
      connection={connected.length ? `${connected.join(', ')} connected` : undefined}
      onSearch={(q) => navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search')}
      linkAs={RouterLink}
      nav={nav}
      orgMenu={authConfig ? (close) => (
        <div role="menu" aria-label="Your organisations">
          <div className="el-menu-head">Your books</div>
          {memberships.map((o) => (
            <button key={o.id} type="button" role="menuitem" className="el-menu-item" aria-current={o.id === orgId}
              onClick={() => { close(); if (o.id !== orgId) void chooseOrg(o.id).then(() => navigate('/')); }}>
              <span>{o.name}</span><span className="el-hint">{o.base_currency}</span>
            </button>
          ))}
          <div className="el-menu-sep" />
          <button type="button" role="menuitem" className="el-menu-item" onClick={() => { close(); useSession.setState({ status: 'no_books' }); }}>Set up new books</button>
        </div>
      ) : undefined}
      userMenu={authConfig ? (close) => (
        <div role="menu" aria-label="Account">
          <div className="el-menu-head">{user.email ?? user.name}</div>
          <button type="button" role="menuitem" className="el-menu-item" onClick={() => { close(); void signOut().then(() => navigate('/sign-in')); }}>Sign out</button>
        </div>
      ) : undefined}
    >
      <div key={orgId ?? 'demo'} style={{ display: 'contents' }}>
      {needsSetup && pathname !== '/setup' && (pathname !== '/' || setupLater()) && (
        <Banner tone="info" action={<Link to="/setup">Finish setting up</Link>}>
          These books aren’t set up yet: add the business details, VAT and the financial year.
        </Banner>
      )}
      <ScreenErrorBoundary resetKey={pathname}>
      <Routes>
        <Route path="/" element={needsSetup && !setupLater() ? null : <Overview />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/settings/organisation" element={<Organisation />} />
        <Route path="/settings/vat-codes" element={<VatCodes />} />
        <Route path="/invoices" element={<InvoicesList />} />
        <Route path="/invoices/new" element={<InvoiceEditor />} />
        <Route path="/invoices/import" element={<ImportInvoices />} />
        <Route path="/invoices/:id" element={<InvoiceView />} />
        <Route path="/invoices/:id/edit" element={<InvoiceEditor />} />
        <Route path="/settings/invoice-design" element={<InvoiceDesign />} />
        <Route path="/inbox" element={<BillInbox />} />
        <Route path="/inbox/:id" element={<BillReview />} />
        <Route path="/bank" element={<Bank />} />
        <Route path="/bank-accounts" element={<BankAccounts />} />
        <Route path="/bank-accounts/:code" element={<BankTransactions />} />
        <Route path="/customers" element={<ContactList key="c" role="customer" />} />
        <Route path="/customers/:id" element={<ContactPage key="c" role="customer" />} />
        <Route path="/receipts/:id" element={<ReceiptPage />} />
        <Route path="/suppliers" element={<ContactList key="s" role="supplier" />} />
        <Route path="/suppliers/:id" element={<ContactPage key="s" role="supplier" />} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/bills/new" element={<InvoiceEditor key="bill-new" type="purchase_bill" />} />
        <Route path="/bills/new-credit" element={<InvoiceEditor key="scn-new" type="purchase_credit" />} />
        <Route path="/bills/:id/edit" element={<InvoiceEditor key="bill-edit" type="purchase_bill" />} />
        <Route path="/bills/:id" element={<BillPage />} />
        {/* #46: credit notes are on the Invoices screen now. */}
        <Route path="/credit-notes" element={<Navigate to="/invoices?show=credit" replace />} />
        <Route path="/credit-notes/new" element={<InvoiceEditor key="cn-new" type="sales_credit" />} />
        <Route path="/credit-notes/:id" element={<InvoiceView />} />
        <Route path="/credit-notes/:id/edit" element={<InvoiceEditor key="cn-edit" type="sales_credit" />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/search" element={<Search />} />
        <Route path="/products" element={<Products />} />
        <Route path="/accounts" element={<ChartOfAccounts />} />
        <Route path="/journals" element={<Journals />} />
        <Route path="/vat" element={<VatReturns />} />
        <Route path="/year-end" element={<YearEnd />} />
        <Route path="/audit" element={<AuditTrail />} />
        <Route path="/statements" element={<Statements />} />
        <Route path="/recurring" element={<Recurring />} />
        <Route path="/settings/currencies" element={<Currencies />} />
        <Route path="/settings/users" element={<Users />} />
        <Route path="/settings/import" element={<Import />} />
        <Route path="/settings/backup" element={<Backups />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </ScreenErrorBoundary>
      </div>
      <Toast message={toast} />
    </AppShell>
  );
}
